import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, ArrowsLeftRight, ChartPie, Target, 
  Sparkle, Heartbeat, ShoppingCart, ChartLine
} from '@phosphor-icons/react';

const LOGO_URL = '/logo-square.png';

const FEATURES = [
  { icon: ArrowsLeftRight, label: 'Expense and income tracking' },
  { icon: ChartPie, label: 'Category-based budgeting' },
  { icon: Target, label: 'Savings goals' },
  { icon: Heartbeat, label: 'Financial health score' },
  { icon: Sparkle, label: 'AI-powered insights' },
  { icon: ShoppingCart, label: 'Affordability analysis' },
  { icon: ChartLine, label: 'Analytics dashboard' },
];

export default function About() {
  return (
    <div className="min-h-screen bg-[#0B0F1A] text-[#F9FAFB]">
      {/* Nav */}
      <header className="w-full max-w-3xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
        <Link 
          to="/" 
          className="flex items-center gap-2 text-[#9CA3AF] hover:text-white transition-colors text-sm" 
          data-testid="about-back-btn"
        >
          <ArrowLeft size={16} />
          Back
        </Link>
        <Link 
          to="/register" 
          className="btn-primary text-sm py-2 px-4" 
          data-testid="about-signup-btn"
        >
          Sign up free
        </Link>
      </header>

      <main className="w-full max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="about-page">
        {/* Logo + Title */}
        <div className="flex items-center gap-4 mb-4">
          <img src={LOGO_URL} alt="UniWallet" className="w-12 h-12 rounded-xl" data-testid="about-logo" />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            About <span className="gradient-text">Uni</span>Wallet
          </h1>
        </div>

        {/* Subtitle */}
        <p className="text-base text-[#9CA3AF] mb-12" data-testid="about-subtitle">
          Smart budgeting and financial insights for students.
        </p>

        {/* Divider line */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-12" />

        {/* Body - paragraph 1 */}
        <section className="mb-8" data-testid="about-body">
          <p className="text-sm sm:text-base text-[#D1D5DB] leading-relaxed">
            UniWallet is a student-focused finance platform built to help users manage money more 
            effectively through budgeting, savings goals, analytics, and AI-powered insights.
          </p>
        </section>

        {/* Body - paragraph 2 */}
        <section className="mb-8">
          <p className="text-sm sm:text-base text-[#D1D5DB] leading-relaxed">
            The platform was designed for university students and young adults who want a simple, 
            modern, and practical way to understand their finances. UniWallet combines transaction 
            tracking, budget planning, savings monitoring, affordability checks, and financial 
            analytics into one experience.
          </p>
        </section>

        {/* Body - paragraph 3 */}
        <section className="mb-12">
          <p className="text-sm sm:text-base text-[#D1D5DB] leading-relaxed">
            Rather than only showing numbers, UniWallet helps users make better financial decisions 
            by turning financial data into clear insights and actionable recommendations.
          </p>
        </section>

        {/* Key Features */}
        <section className="mb-14" data-testid="about-features">
          <h2 className="text-lg font-semibold mb-6">Key Features</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {FEATURES.map(({ icon: Icon, label }) => (
              <div 
                key={label} 
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#111827] border border-white/5 hover:border-white/10 transition-colors"
                data-testid={`feature-${label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-8 h-8 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center flex-shrink-0">
                  <Icon size={16} weight="duotone" className="text-[#3B82F6]" />
                </div>
                <span className="text-sm font-medium text-[#E5E7EB]">{label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-10" />

        {/* CTA */}
        <div className="text-center mb-12">
          <Link 
            to="/register" 
            className="btn-primary inline-flex items-center gap-2 text-sm py-3 px-8" 
            data-testid="about-cta-btn"
          >
            Get started free
            <ArrowRight size={16} weight="bold" />
          </Link>
        </div>

        {/* Footer Credit */}
        <footer className="text-center pt-6 border-t border-white/5" data-testid="about-footer">
          <p className="text-xs text-[#6B7280]">
            Developed by Zishan Thiruvangaden Naufel
          </p>
        </footer>
      </main>
    </div>
  );
}
